import * as project from "./project";
import type {
  AbsoluteElementPath,
  ChildIdentifier,
  ElementIdentifier,
  NodeIdentifier,
  ParentIdentifier,
  RelativeElementPath,
  SchemaIdentifier,
} from "./project";

// TODO: Create custom types for schema, groups, fields and rules.
// The goal is to have a single type that can represent both the arbeitskopie and the version.
// This makes it easy to switch from a version to an arbeitskopie and vice versa.
// Alternatively: Just use a tagged union type for the two types.

export enum SaveStatus {
  SAVING = 0,
  SAVED = 1,
}

export interface EditorBackend {
  saveSchema(schema: project.Schema): SaveStatus;
  saveGroup(group: project.Group): SaveStatus;
  saveField(field: project.Field): SaveStatus;
  saveRule(rule: project.Rule): SaveStatus;

  createNewSchemaWithFimId(fimId: string): project.SchemaArbeitskopie;
  createNewGroup(): project.GroupArbeitskopie;
  createNewField(): project.FieldArbeitskopie;
  createNewRule(): project.RuleArbeitskopie;

  importGroup(fimId: string, fimVersion: string): Promise<project.GroupImport>;
  importField(fimId: string, fimVersion: string): Promise<project.FieldImport>;
}

export type Diagnostic = {
  type: "warning" | "error";
  element: ElementIdentifier;
  message: string;
};

export type SelectCallback = (path: AbsoluteElementPath) => void;

// The data structure containing all the central state of the project.
// This includes:
//
// - All of the schema data
// - Diagnostics
// - the current selection mode
//
// All changes to any of these resources must always be synced back into the store via the
// provided methods. This will trigger all necessary events, which can be subscribed for by
// any component that needs to react to changes in the project.
//
// ### Selection Mode
//
// When selecting an element in the edit tree, the resulting events fired depend on the active `selectionMode`.
//
// - `normal`: This will fire the `active` event with the selected element. This is the default mode.
// - `pick`: The full path within the schema of the selected element will be passed to the `selectCallback`.
// This callback ist provided when entering `pick` mode.
//
// ### Content Events
//
// - `schema`: When the schema was changed
// - `<schemaId>`: When the schema was changed (same as `schema`)
// - `fields`: When a field was added/removed from the collection
// - `groups`: When a group was added/removed from the collection
// - `fieldId/groupId`: When a specific group/field was changed
// - `project`: When any change has happend to any of the resources above
//
// ### UI Events
//
// - `active`: When the active element has changed
// - `selectionMode`: When the selection mode has changed
//
// ### Storage Events
//
// - `saveStatus`: When the save status has changed
//
export class ProjectStore extends EventTarget {
  // TODO: Allow groups, versioned groups and versioned schemas
  public readonly rootIdentifier: SchemaIdentifier;

  public schema: project.Schema;
  public groups: Record<string, project.Group>;
  public fields: Record<string, project.Field>;
  public rules: Record<string, project.Rule>;

  public activeElement: ElementIdentifier;

  public diagnostics: Diagnostic[];

  public selectionMode: "normal" | "pick" = "normal";
  public selectCallback: SelectCallback | undefined = undefined;

  public saveStatus: SaveStatus = SaveStatus.SAVED;

  constructor(
    document: project.Document,
    public backend: EditorBackend,
  ) {
    super();

    if (document.type === "schema") {
      project.assert(document.schema !== undefined, "Schema is undefined");

      this.rootIdentifier = {
        type: "schema",
        fimId: document.schema.fimId,
      };

      this.schema = document.schema;
      this.groups = document.groups;
      this.fields = document.fields;
      this.rules = document.rules;
    } else {
      throw Error("Versioned documents are not supported");
    }

    this.activeElement = this.rootIdentifier;

    this.diagnostics = [];
    this.updateDiagnostics();
  }

  public setSaveStatus(status: SaveStatus) {
    if (this.saveStatus !== status) {
      this.saveStatus = status;
      this.dispatchEvent(new Event("saveStatus"));
    }
  }

  private updateDiagnostics() {
    this.diagnostics = [];

    if (this.schema.children.length === 0) {
      this.diagnostics.push({
        type: "error",
        element: { type: "schema", fimId: this.schema.fimId },
        message: "Ein Schema braucht mindestens ein Unterelement.",
      });
    }

    for (const group of Object.values(this.groups)) {
      if (group.children.length === 0) {
        this.diagnostics.push({
          type: "error",
          element: { type: "group", fimId: group.fimId },
          message: "Eine Datenfeldgruppe braucht mindestens ein Unterelement.",
        });
      }
    }

    this.dispatchEvent(new Event("diagnostics"));
  }

  public enterPickMode(selectCallback: SelectCallback) {
    this.selectCallback = selectCallback;

    if (this.selectionMode !== "pick") {
      this.selectionMode = "pick";
      this.dispatchEvent(new CustomEvent("selectionMode", { detail: "pick" }));
    }
  }

  public enterNormalMode() {
    this.selectCallback = undefined;

    if (this.selectionMode !== "normal") {
      this.selectionMode = "normal";
      this.dispatchEvent(
        new CustomEvent("selectionMode", { detail: "normal" }),
      );
    }
  }

  // Select an element in the schema tree.
  // This will fire the appropriate events based on the current selection mode.
  public selectElement(elementPath: AbsoluteElementPath) {
    if (this.selectionMode === "normal") {
      this.setActiveElement(elementPath[elementPath.length - 1]);
    } else if (this.selectionMode === "pick") {
      project.assert(
        this.selectCallback !== undefined,
        "selectCallback is undefined",
      );

      this.selectCallback(elementPath);
      this.enterNormalMode();
    }
  }

  // Set the active element in the project.
  // This will fire the `active` event with the new element.
  //
  // This is basically the same as `selectElement` during `normal` selection mode, but
  // can be used at locations where selecting an element should always result in the `active` event.
  // This will also change the `selectionMode` back to `normal`.
  public setActiveElement(element: ElementIdentifier) {
    this.enterNormalMode();

    if (this.activeElement.fimId !== element.fimId) {
      this.activeElement = element;
      this.dispatchEvent(new CustomEvent("active", { detail: element }));
    }
  }

  public toDocument(): project.Document {
    return {
      type: "schema",
      schema: this.schema,
      groups: this.groups,
      fields: this.fields,
      rules: this.rules,
    };
  }

  public updateSchema(schema: project.SchemaArbeitskopie) {
    this.schema = schema;

    const saveStatus = this.backend.saveSchema(schema);
    this.setSaveStatus(saveStatus);

    this.dispatchEvent(new Event("schema"));
    this.dispatchEvent(new Event(schema.fimId));
    this.dispatchEvent(new Event("project"));
  }

  public updateGroup(group: project.GroupArbeitskopie) {
    this.groups[group.fimId] = group;

    const saveStatus = this.backend.saveGroup(group);
    this.setSaveStatus(saveStatus);

    this.dispatchEvent(new Event(group.fimId));
    this.dispatchEvent(new Event("project"));
    this.updateDiagnostics();
  }

  public updateField(field: project.FieldArbeitskopie) {
    this.fields[field.fimId] = field;

    const saveStatus = this.backend.saveField(field);
    this.setSaveStatus(saveStatus);

    this.dispatchEvent(new Event(field.fimId));
    this.dispatchEvent(new Event("project"));
    this.updateDiagnostics();
  }

  public updateRule(rule: project.RuleArbeitskopie) {
    this.rules[rule.fimId] = rule;

    const saveStatus = this.backend.saveRule(rule);
    this.setSaveStatus(saveStatus);

    this.dispatchEvent(new Event(rule.fimId));
    this.dispatchEvent(new Event("project"));
    this.updateDiagnostics();
  }

  public getRuleParent(ruleId: string): NodeIdentifier {
    for (const rule of this.schema.rules) {
      if (rule.fimId === ruleId) {
        return { type: "schema", fimId: this.schema.fimId };
      }
    }

    for (const group of Object.values(this.groups)) {
      for (const rule of group.rules) {
        if (rule.fimId === ruleId) {
          return { type: "group", fimId: group.fimId };
        }
      }
    }

    for (const field of Object.values(this.fields)) {
      for (const rule of field.rules) {
        if (rule.fimId === ruleId) {
          return { type: "field", fimId: field.fimId };
        }
      }
    }

    throw Error(`Rule ${ruleId} not found in any group, field or schema`);
  }

  public addNewGroup(target: ParentIdentifier): project.GroupArbeitskopie {
    const group = this.backend.createNewGroup();
    this.groups[group.fimId] = group;

    this.addChild(target, {
      type: "group",
      element: {
        fimId: group.fimId,
      },
      cardinality: { type: "value", optional: false },
    });

    this.dispatchEvent(new Event("groups"));

    return group;
  }

  public addNewField(target: ParentIdentifier): project.FieldArbeitskopie {
    const field = this.backend.createNewField();
    this.fields[field.fimId] = field;

    this.addChild(target, {
      type: "field",
      element: {
        fimId: field.fimId,
      },
      cardinality: { type: "value", optional: false },
    });

    this.dispatchEvent(new Event("fields"));

    return field;
  }

  public createRule(element: NodeIdentifier): project.RuleArbeitskopie {
    const rule = this.backend.createNewRule();
    this.rules[rule.fimId] = rule;

    switch (element.type) {
      case "schema":
        {
          project.assert(this.schema.type === "arbeitskopie");
          this.schema.rules.push({ fimId: rule.fimId, fimVersion: undefined });
          this.updateSchema(this.schema);
        }
        break;
      case "group":
        {
          const group = this.groups[element.fimId];
          project.assert(group.type === "arbeitskopie");
          group.rules.push({ fimId: rule.fimId, fimVersion: undefined });
          this.updateGroup(group);
        }
        break;
      case "field": {
        {
          const field = this.fields[element.fimId];
          project.assert(field.type === "arbeitskopie");
          field.rules.push({ fimId: rule.fimId, fimVersion: undefined });
          this.updateField(field);
        }
      }
    }

    this.dispatchEvent(new Event("rules"));

    return rule;
  }

  public removeRuleFromElement(element: NodeIdentifier, ruleId: string) {
    // TODO: When to completely remove the rule?
    switch (element.type) {
      case "schema":
        {
          project.assert(this.schema.type === "arbeitskopie");
          this.schema.rules = this.schema.rules.filter(
            (ref) => ref.fimId !== ruleId,
          );
          this.updateSchema(this.schema);
        }
        break;
      case "group":
        {
          const group = this.groups[element.fimId];
          project.assert(group.type === "arbeitskopie");
          group.rules = group.rules.filter((ref) => ref.fimId !== ruleId);
          this.updateGroup(group);
        }
        break;
      case "field": {
        {
          const field = this.fields[element.fimId];
          project.assert(field.type === "arbeitskopie");
          field.rules = field.rules.filter((ref) => ref.fimId !== ruleId);
          this.updateField(field);
        }
      }
    }
  }

  public async importGroupFromFimPortal(
    target: ParentIdentifier,
    fimId: string,
    fimVersion: string,
  ) {
    const groupImport = await this.backend.importGroup(fimId, fimVersion);

    // Import all elements into the store
    // TODO: Handle conflicts
    for (const group of groupImport.groups) {
      this.groups[group.fimId] = group;
    }
    for (const field of groupImport.fields) {
      this.fields[field.fimId] = field;
    }
    for (const rule of groupImport.rules) {
      this.rules[rule.fimId] = rule;
    }

    this.addChild(target, {
      type: "group",
      element: groupImport.rootGroupIdentifier,
      cardinality: { type: "value", optional: false },
    });
  }

  public async importFieldFromFimPortal(
    target: ParentIdentifier,
    fimId: string,
    fimVersion: string,
  ) {
    const fieldImport = await this.backend.importField(fimId, fimVersion);

    // Import all elements into the store
    // TODO: Handle conflicts
    this.fields[fieldImport.field.fimId] = fieldImport.field;
    for (const rule of fieldImport.rules) {
      this.rules[rule.fimId] = rule;
    }

    this.addChild(target, {
      type: "field",
      element: {
        fimId: fieldImport.field.fimId,
        fimVersion:
          fieldImport.field.type === "version"
            ? fieldImport.field.fimVersion
            : undefined,
      },
      cardinality: { type: "value", optional: false },
    });
  }

  /**
   * Update the children of a group or schema.
   * This will modify the target in-place and trigger all necessary events.
   */
  private updateChildren(
    parent: ParentIdentifier,
    action: (
      target: project.GroupArbeitskopie | project.SchemaArbeitskopie,
    ) => void,
  ) {
    switch (parent.type) {
      case "schema":
        {
          project.assert(this.schema.type === "arbeitskopie");
          action(this.schema);
          this.updateSchema(this.schema);
        }
        break;
      case "group":
        {
          const group = this.groups[parent.fimId];
          project.assert(group.type === "arbeitskopie");
          action(group);
          this.updateGroup(group);
        }
        break;
    }

    this.dispatchEvent(new Event("project"));
    this.updateDiagnostics();
  }

  private addChild(target: ParentIdentifier, child: project.Child) {
    this.updateChildren(target, (target) => {
      target.children.push(child);
    });

    this.setActiveElement({ type: child.type, fimId: child.element.fimId });
  }

  public removeChild(parent: ParentIdentifier, child: ChildIdentifier) {
    this.updateChildren(parent, (target) => {
      target.children = target.children.filter(
        (c) => c.element.fimId !== child.fimId,
      );
    });
  }

  public moveChildUp(parent: ParentIdentifier, child: ChildIdentifier) {
    this.updateChildren(parent, (target) => {
      const index = target.children.findIndex(
        (c) => c.element.fimId === child.fimId,
      );

      project.assert(
        index !== -1,
        `Child ${child.fimId} not found in parent ${target.fimId}.`,
      );

      // Already the first element
      if (index === 0) {
        return;
      }

      const temp = target.children[index - 1];
      target.children[index - 1] = target.children[index];
      target.children[index] = temp;
    });
  }

  public moveChildDown(parent: ParentIdentifier, child: ChildIdentifier) {
    this.updateChildren(parent, (target) => {
      const index = target.children.findIndex(
        (c) => c.element.fimId === child.fimId,
      );

      project.assert(
        index !== -1,
        `Child ${child.fimId} not found in parent ${target.fimId}.`,
      );

      // Already the last element
      if (index === target.children.length - 1) {
        return;
      }

      const temp = target.children[index + 1];
      target.children[index + 1] = target.children[index];
      target.children[index] = temp;
    });
  }

  // Return the set of all groups, which directly or
  // indirectly contain the specified `elementId`.
  public getAllParents(elementId: string): Set<string> {
    const elementToParents: Map<string, string[]> = new Map();

    for (const group of Object.values(this.groups)) {
      for (const child of group.children) {
        let parentList = elementToParents.get(child.element.fimId);
        if (parentList === undefined) {
          parentList = [];
          elementToParents.set(child.element.fimId, parentList);
        }

        parentList.push(group.fimId);
      }
    }

    const toCheck = elementToParents.get(elementId);
    if (toCheck === undefined) {
      return new Set();
    }

    const allParents: Set<string> = new Set();

    // This mutates the list of parents from the orriginal child.
    // But since we do not have cycles, we will never need this list
    // again here and can therefore just reuse it.
    while (toCheck.length > 0) {
      const groupId = toCheck.pop()!;
      allParents.add(groupId);

      const parents = elementToParents.get(groupId);
      if (parents !== undefined) {
        for (const parent of parents) {
          if (!allParents.has(parent)) {
            toCheck.push(parent);
          }
        }
      }
    }

    return allParents;
  }
}

export function getRelativeChildPath(
  parent: NodeIdentifier,
  childPath: AbsoluteElementPath,
): RelativeElementPath {
  const parentPosition = childPath.findIndex((element) => {
    if (element.type === "schema") {
      return parent.type === "schema" && element.fimId === parent.fimId;
    } else {
      return parent.type === "group" && element.fimId === parent.fimId;
    }
  });

  if (parentPosition === -1) {
    throw Error(`Parent ${parent} not found in path ${childPath}`);
  }

  return childPath.slice(parentPosition) as RelativeElementPath;
}
